import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../../utils/apiBase';
import { studioWhen } from '../../utils/studioDate';
import EmailPreviewGallery from './EmailPreviewGallery';
import './QuoteHistory.css';
import './StudioInbox.css';

interface InboxRow {
  quoteId: string;
  quoteNumber: string;
  clientName: string;
  status: string;
  unread: boolean;
  needsResponse: boolean;
  lastText: string;
  lastAt: string;
}

const ClientMessages = ({ initialFilter = 'all', onOpenShoot }: {
  initialFilter?: string;
  onOpenShoot?: (quoteId: string) => void;
}) => {
  const [rows, setRows] = useState<InboxRow[]>([]);
  const [query, setQuery] = useState('');
  const [type, setType] = useState(initialFilter || 'all');
  const [error, setError] = useState('');

  const loadInbox = async (nextQuery = query, nextType = type) => {
    const params = new URLSearchParams();
    if (nextQuery.trim()) params.set('q', nextQuery.trim());
    if (nextType !== 'all') params.set('type', nextType);
    const response = await fetch(`${API_BASE_URL}/studio/messages?${params.toString()}`);
    if (response.status === 403 || response.status === 401) {
      setError('Client messages are available from the studio login.');
      return;
    }
    if (!response.ok) {
      setError('Messages could not be loaded.');
      return;
    }
    setRows(await response.json());
  };

  useEffect(() => {
    loadInbox().catch(() => setError('Messages could not be loaded.'));
  }, []);

  useEffect(() => {
    if (initialFilter) setType(initialFilter);
  }, [initialFilter]);

  const rowState = (row: InboxRow) => {
    if (row.needsResponse) return 'Needs reply';
    if (row.unread) return 'Unread';
    if (row.status === 'resolved') return 'Resolved';
    if (row.status === 'awaiting-client') return 'Awaiting client';
    return 'Open';
  };

  return (
    <div className="quote-builder-wizard quote-history studio-inbox-page" data-testid="messages-list">
      <div className="quote-builder-header">
        <h2 className="studio-duplicate">Client messages</h2>
        <p>Who is waiting for a reply. Open a row to message them on their shoot page.</p>
      </div>
      <div className="quote-history-toolbar">
        <input className="form-input" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search client or quotation" aria-label="Search conversations" />
        <select className="form-input" value={type} onChange={(event) => setType(event.target.value)} aria-label="Filter conversations">
          <option value="all">All conversations</option>
          <option value="unread">Unread</option>
          <option value="awaiting-carmen">Needs reply</option>
          <option value="resolved">Resolved</option>
        </select>
        <button type="button" className="btn-secondary" onClick={() => loadInbox(query, type)}>Search</button>
      </div>
      {error ? <p className="quote-history-note">{error}</p> : null}
      <div className="studio-inbox">
        <div className="studio-inbox-list studio-inbox-list-full">
          {rows.map((row) => (
            <button
              type="button"
              className="studio-inbox-row"
              key={row.quoteId}
              data-testid={`message-row-${row.quoteId}`}
              onClick={() => onOpenShoot?.(row.quoteId)}
            >
              <span className="studio-inbox-name">{row.clientName || 'Client'}</span>
              <span className="studio-inbox-ref">{row.quoteNumber}</span>
              <span className={`studio-inbox-status${row.needsResponse ? ' is-urgent' : ''}`}>{rowState(row)}</span>
              <span className="studio-inbox-preview">{row.lastText || 'No message yet'}</span>
              <span className="studio-inbox-meta">{row.lastAt ? studioWhen(row.lastAt) : 'No activity yet'}</span>
            </button>
          ))}
          {rows.length === 0 ? (
            <p className="quote-history-empty">
              {query || type !== 'all' ? 'No conversations match this search.' : 'No client messages yet. They appear here when a client writes on their quotation page.'}
            </p>
          ) : null}
        </div>
      </div>
      <EmailPreviewGallery />
    </div>
  );
};

export default ClientMessages;
